import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


export interface CommissionDto {
  id: string;
  cycleId: string;
  winningDiscount: number;
  grossCommission: number;
  tdsAmount: number;
  netCommission: number;
  status: 'COMPUTED' | 'APPROVED' | 'PAYOUT_INSTRUCTED' | 'PAID';
  payoutInstructionId: string | null;
}

export interface CommissionRegisterSummaryDto {
  totalEarned: number;
  totalPaid: number;
  outstanding: number;
  records: CommissionDto[];
}

export const commissionKeys = {
  all: ['commission'] as const,
  scheme: (schemeId: string) => [...commissionKeys.all, 'scheme', schemeId] as const,
  detail: (schemeId: string, cycleId: string) => [...commissionKeys.all, 'detail', schemeId, cycleId] as const,
};

export function useSchemeCommissions(schemeId: string) {
  return useQuery({
    queryKey: commissionKeys.scheme(schemeId),
    queryFn: async () => {
      const response = await fetcher.get<CommissionRegisterSummaryDto>(
        `/staff/v1/commission/schemes/${schemeId}/commission`
      );
      return response;
    },
    enabled: !!schemeId,
  });
}

export function useCycleCommissionDetail(schemeId: string, cycleId: string) {
  return useQuery({
    queryKey: commissionKeys.detail(schemeId, cycleId),
    queryFn: async () => {
      const response = await fetcher.get<CommissionDto>(
        `/staff/v1/commission/schemes/${schemeId}/cycles/${cycleId}/commission`
      );
      return response;
    },
    enabled: !!schemeId && !!cycleId,
  });
}

export function useApproveCommission(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetcher.post<{ success: boolean; payoutInstructionId: string }>(
        `/staff/v1/commission/commission/${id}/approve`,
        {}
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.scheme(schemeId) });
    },
  });
}
